import { useTranslations } from "@fenetech/translations";
import { AlertColor, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import useWait from "helpers/context/Page/useWait";
import { ESignatureCaptureMethodEnum, ThemeColorEnum } from "helpers/enums";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { BlankESignatureEnvelopeParams } from "./BlankESignatureEnvelopeParams";
import ESignatureAPI from "components/Setup/ESignature/ESignatureAPI";
import { IESignatureEnvelopeParams, IESignatureReportSetting, IESignatureResult } from "models/IESignature";
import { GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";
import useQuoteData from "components/Quotes/useQuoteData";
import LabeledText from "components/Common/LabeledText";
import { useSearchParams } from "react-router-dom";
import { useEffectOnLoad } from "helpers/hooks/useEffectOnLoad";
import ESignatureDialogForm from "./ESignatureDialogForm";
import useReports from "helpers/context/SelectionValues/useReports";

interface IProps {
    isOpen: boolean,
    reports: IESignatureReportSetting[],
    setESignatureDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
    launchESignatureConsentLogin: () => Promise<void>,
    refreshESignatureDocument: () => Promise<void>,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const ESignDialog: React.FC<IProps> = ({ isOpen, reports, setESignatureDialogOpen, launchESignatureConsentLogin, refreshESignatureDocument, setAlert }: IProps) => {

    const tm = useTranslations();
    const wait = useWait();
    const { quote } = useQuoteData();
    const [query] = useSearchParams();
    const { reportSelections } = useReports("web", "quote");

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [eSignatureSignedIn, setESignatureSignedIn] = useState<boolean | null>(null);
    const [eSignatureCaptureMethod, setESignatureCaptureMethod] = useState<ESignatureCaptureMethodEnum>(ESignatureCaptureMethodEnum.Email);

    const formMethods = useForm({ mode: "onChange", defaultValues: BlankESignatureEnvelopeParams });
    const { handleSubmit, reset, getValues } = formMethods;

    const eSignMessage: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "eSignMessage") ?? null;
    }, [query]);

    useEffectOnLoad(() => {
        if (!isNullOrWhiteSpace(eSignMessage)) {
            setAlert([eSignMessage!, "error"]);
        }
    })

    useEffect(() => {
        wait.Show(isLoading);
    }, [wait, isLoading]);

    const callbackURI = useMemo(() => {
        if (quote) {
            return document.getElementsByTagName("base")[0].href + `quotes/?oKey=${quote.oKey}`;
        }
        return "";
    }, [quote]);

    const checkESignatureIsSignedIn = useCallback(async () => {
        setIsLoading(true);
        try {
            const pingResponse = await ESignatureAPI.PingTest(quote!.mfgCustomerID, quote!.mfgSiteID);

            if (pingResponse.loginRequested && isNullOrWhiteSpace(eSignMessage)) {
                await launchESignatureConsentLogin();
                return false;
            } else if (!isNullOrWhiteSpace(pingResponse.message)) {
                setAlert([tm.Get(pingResponse.message), "error"]);
                return false;
            } else {
                return pingResponse.payload;
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [quote, eSignMessage, tm, launchESignatureConsentLogin, setAlert]);

    const validReports = useMemo(() => {
        //If a report does not have an anchorTag or Units, DocuSign is not going to work
        let validReports = reports.filter(r => !isNullOrWhiteSpace(r.anchorTag) && !isNullOrWhiteSpace(r.units));

        //Do not get report names from the DB since base reports are hard coded in frontend code
        validReports.forEach(rs => {
            if (isNullOrWhiteSpace(rs.reportName)) {
                rs.reportName = reportSelections.find(r => r.reportID.toString() === rs.reportID)?.menuCaption ?? "";
            }
        });

        return validReports.sort((a, b) => a.reportName.localeCompare(b.reportName));;
    }, [reports, reportSelections]);

    const getEnvelopeDefaults = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await ESignatureAPI.GetEnvelopeDefaults(quote!.oKey);
            
            const defaults: IESignatureEnvelopeParams = res ?? BlankESignatureEnvelopeParams

            if (!isNullOrWhiteSpace(defaults.reportID)) {
                if (validReports.length === 0 || validReports.findIndex(r=> r.reportID === defaults.reportID) === -1) {
                    //edge case: if we have no valid reports, or the default value is not a valid report, unset the default value
                    defaults.reportID = "";
                }
            }

            reset(defaults);
        }
        finally {
            setIsLoading(false);
        }
    }, [quote, reset, validReports]);

    const onDialogOpen = useCallback(async () => {
        const isSignedIn = await checkESignatureIsSignedIn();
        setESignatureSignedIn(isSignedIn);

        if (isSignedIn) {
            await getEnvelopeDefaults();
        }
    }, [checkESignatureIsSignedIn, getEnvelopeDefaults]);

    useEffect(() => {
        if (isOpen && quote && eSignatureSignedIn === null) {
            onDialogOpen();
        }
    }, [isOpen, quote, eSignatureSignedIn, onDialogOpen]);

    const formatBodyPreviewText = useCallback((templateText: string) => {
        const formData = getValues();

        let preview: string = templateText;
        preview = preview.replace("<SIGNER_NAME>", formData.signerName);
        preview = preview.replace("<SENDER_NAME>", formData.senderName);

        return preview;
    }, [getValues]);

    const buttonText = useMemo(() => {
        return eSignatureCaptureMethod === ESignatureCaptureMethodEnum.Email ? tm.Get("Email E-Signature Document") : tm.Get("Capture E-Signature");
    }, [eSignatureCaptureMethod, tm]);

    const onEmailSent = useCallback(async (res: IESignatureResult<boolean>) => {
        if (res.loginRequested && eSignatureSignedIn !== false) {
            await launchESignatureConsentLogin();
        } else if (!isNullOrWhiteSpace(res.message)) {
            setAlert([tm.Get(res.message), "error"]);
        } else {
            setAlert([tm.Get("Email sent"), "success"]);
            await refreshESignatureDocument();
        }
    }, [eSignatureSignedIn, launchESignatureConsentLogin, tm, setAlert, refreshESignatureDocument]);

    const onEmbeddedURIGenerated = useCallback(async (res: IESignatureResult<string>) => {
        if (res.loginRequested && eSignatureSignedIn !== false) {
            await launchESignatureConsentLogin();
        } else if (!isNullOrWhiteSpace(res.message)) {
            setAlert([tm.Get(res.message), "error"]);
        } else {
            window.location.assign(res.payload);
        }
    }, [eSignatureSignedIn, launchESignatureConsentLogin, setAlert, tm]);

    const onSubmit = useCallback(async (formData: IESignatureEnvelopeParams) => {
        setIsLoading(true);
        formData.emailBody = formatBodyPreviewText(formData.emailBody);
        try {
            if (quote) {
                if (eSignatureCaptureMethod === ESignatureCaptureMethodEnum.Email) {
                    const res: IESignatureResult<boolean> = await ESignatureAPI.SendEmail(quote.mfgCustomerID, quote.mfgSiteID, quote.oKey, formData);
                    await onEmailSent(res);
                } else {
                    const res: IESignatureResult<string> = await ESignatureAPI.GenerateEmbeddedURI(quote.oKey, callbackURI, formData);
                    await onEmbeddedURIGenerated(res);
                }
            }
        }
        finally {
            setIsLoading(false);
            setESignatureDialogOpen(false);
        }
    }, [quote, setESignatureDialogOpen, eSignatureCaptureMethod, callbackURI, formatBodyPreviewText, onEmailSent, onEmbeddedURIGenerated]);

    return <>
        {quote &&
            <Dialog open={isOpen} fullWidth onClose={() => setESignatureDialogOpen(false)}>
                <FormProvider {...formMethods}>
                    <form id="ESignDialog" onSubmit={handleSubmit(onSubmit)}>
                        <DialogTitle>{tm.Get("E-Signature")}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={2}>
                                {eSignatureSignedIn === false && 
                                    <LabeledText
                                        isChecked={false}
                                        text={tm.Get("Detected issues connecting to API services.")}
                                        label=""
                                        isLoading={isLoading}
                                    />
                                }

                                <ESignatureDialogForm
                                    reports={validReports}
                                    eSignatureCaptureMethod={eSignatureCaptureMethod}
                                    setESignatureCaptureMethod={setESignatureCaptureMethod}
                                    formatBodyPreviewText={formatBodyPreviewText}
                                    disabled={eSignatureSignedIn === false}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="contained" color={ThemeColorEnum.Primary} onClick={(_) => setESignatureDialogOpen(false)}>{tm.Get("Cancel")}</Button>
                            <Button variant="contained" color={ThemeColorEnum.Secondary} type="submit" disabled={eSignatureSignedIn === false}>{buttonText}</Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        }
    </>
}
export default ESignDialog;